import { useNavigate } from 'react-router-dom';
import LegalLinks from '@/components/LegalLinks';
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppCardHeader,
  AppPage,
  AppPageIntro,
  PUBLIC_BETA_URLS,
  AppSectionHeader,
  AppTextLinkButton,
  AppText,
  appTextLinkButtonClassName,
} from '../../ui-foundation';
import type {
  SharedLegalSection,
  SharedPublicRouteKey,
  SharedRichParagraph,
} from '../../ui-foundation';

type PublicLegalAction = {
  label: string;
  onPress: () => void;
  tone?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive';
};

type PublicLegalPageProps = {
  title: string;
  subtitle: string;
  introBody: SharedRichParagraph;
  sections: SharedLegalSection[];
  actions?: PublicLegalAction[];
};

const WEB_PUBLIC_ROUTE_PATHS: Record<SharedPublicRouteKey, string> = {
  about: '/about',
  privacy: '/privacy',
  gdpr: '/gdpr',
  terms: '/terms',
  communityGuidelines: '/community-guidelines',
  contactSupport: '/contact-complaints',
  settings: '/settings',
  documents: '/documents',
  communityFeed: '/community',
};

function RichParagraph({
  paragraph,
  navigate,
}: {
  paragraph: SharedRichParagraph;
  navigate: ReturnType<typeof useNavigate>;
}) {
  return (
    <p className="text-sm leading-relaxed text-muted-foreground">
      {paragraph.map((part, index) => {
        const key = `${part.type}-${index}`;

        if (part.type === 'text') {
          return <span key={key}>{part.text}</span>;
        }

        if (part.type === 'route') {
          return (
            <AppTextLinkButton key={key} onClick={() => navigate(WEB_PUBLIC_ROUTE_PATHS[part.key])}>
              {part.label}
            </AppTextLinkButton>
          );
        }

        if (part.type === 'email') {
          return (
            <a key={key} href={`mailto:${part.email}`} className={appTextLinkButtonClassName}>
              {part.label ?? part.email}
            </a>
          );
        }

        return (
          <a key={key} href={part.href} target="_blank" rel="noreferrer" className={appTextLinkButtonClassName}>
            {part.label}
          </a>
        );
      })}
    </p>
  );
}

function PublicLegalSectionCard({
  section,
  navigate,
}: {
  section: SharedLegalSection;
  navigate: ReturnType<typeof useNavigate>;
}) {
  return (
    <AppCard>
      <AppCardHeader>
        <AppSectionHeader title={section.title} />
      </AppCardHeader>
      <AppCardBody>
        <div className="space-y-2 text-sm leading-relaxed text-muted-foreground">
          {section.paragraphs.map((paragraph, index) => (
            <RichParagraph key={`${section.title}-${index}`} paragraph={paragraph} navigate={navigate} />
          ))}
        </div>
      </AppCardBody>
    </AppCard>
  );
}

export default function PublicLegalPage({
  title,
  subtitle,
  introBody,
  sections,
  actions = [],
}: PublicLegalPageProps) {
  const navigate = useNavigate();

  return (
    <AppPage className="animate-fade-in">
      <AppPageIntro title={title} subtitle={subtitle} />

      <AppCard>
        <AppCardHeader>
          <AppSectionHeader title={title} subtitle="Adulting · Last updated April 2026" />
        </AppCardHeader>
        <AppCardBody>
          <div className="space-y-2 text-sm leading-relaxed text-muted-foreground">
            <RichParagraph paragraph={introBody} navigate={navigate} />
          </div>
        </AppCardBody>
      </AppCard>

      {sections.map((section) => (
        <PublicLegalSectionCard key={section.title} section={section} navigate={navigate} />
      ))}

      {actions.length ? (
        <AppCard>
          <AppCardHeader>
            <AppSectionHeader title="Where to go next" subtitle="Use the matching Adulting screens and account controls." />
          </AppCardHeader>
          <AppCardBody>
            <AppButtonRow>
              {actions.map((action) => (
                <AppButton key={action.label} tone={action.tone ?? 'outline'} size="sm" onPress={action.onPress}>
                  {action.label}
                </AppButton>
              ))}
            </AppButtonRow>
          </AppCardBody>
        </AppCard>
      ) : null}

      <div className="pt-2">
        <LegalLinks hideWhenAuthenticated tone="public" />
      </div>

      <AppText fontSize="$xs" color="$colorMuted" textAlign="center">
        Questions about privacy, data rights, terms, or complaints? Contact &amp; complaints or {PUBLIC_BETA_URLS.supportEmail}
      </AppText>
    </AppPage>
  );
}
